import { Component, computed, inject, OnInit } from '@angular/core';
import { DecimalPipe, NgTemplateOutlet } from '@angular/common';

import { BookingStore } from '../../../../pages/booking/store';

import { AncillariesType, FlightType, PassengerType } from '../../../enums';
import { PriceFlights } from '../../../models/api/flight';

type TotalAncillary = {
  type: AncillariesType;
  name: string;
  price: number;
  leg: FlightType;
  count: number;
  totalPrice: number;
};

@Component({
  selector: 'app-ticket-prices',
  templateUrl: './ticket-prices.component.html',
  standalone: true,
  imports: [NgTemplateOutlet, DecimalPipe],
})
export class TicketPricesComponent implements OnInit {
  private _bookingStore = inject(BookingStore);
  readonly selectFlightState = this._bookingStore.selectFlight;

  readonly flightPriceData = computed(() => {
    const adult = this.selectFlightState.adult();
    const child = this.selectFlightState.child();
    const infant = this.selectFlightState.infant();

    let result: {
      originalAdult: number;
      adult: PriceFlights | undefined;
      originalChild: number;
      child: PriceFlights | undefined;
      originalInfant: number;
      infant: PriceFlights | undefined;
      ancillaries: number;
    } = {
      originalAdult: 0,
      adult: undefined,
      originalChild: 0,
      child: undefined,
      originalInfant: 0,
      infant: undefined,
      ancillaries: 0,
    };

    let prices = this.selectFlightState.selectedFlight()?.flights[0].prices;
    if (
      this.selectFlightState.showSmartCombo() &&
      this.selectFlightState.selectedSmartCombo()
    ) {
      prices = this.selectFlightState.selectedSmartCombo()?.prices;
    }

    if (prices) {
      if (adult > 0) {
        const adultPrice = prices!.find((p) => p.type === PassengerType.Adult);
        if (adultPrice) {
          result.adult = adultPrice;

          if (adultPrice.discount > 0) {
            result.originalAdult = adultPrice.price + adultPrice.discount;
          }
        }
      }

      if (child > 0) {
        const childPrice = prices!.find((p) => p.type === PassengerType.Child);
        if (childPrice) {
          result.child = childPrice;

          if (childPrice.discount > 0) {
            result.originalChild = childPrice.price + childPrice.discount;
          }
        }
      }

      if (infant > 0) {
        const infantPrice = prices!.find(
          (p) => p.type === PassengerType.Infant,
        );
        if (infantPrice) {
          result.infant = infantPrice;

          if (infantPrice.discount > 0) {
            result.originalInfant = infantPrice.price + infantPrice.discount;
          }
        }
      }
    }

    result.ancillaries = this.flightAncillaries().reduce(
      (a, b) => a + b.totalPrice,
      0,
    );

    return result;
  });
  readonly roundTripFlightPriceData = computed(() => {
    const adult = this.selectFlightState.adult();
    const child = this.selectFlightState.child();
    const infant = this.selectFlightState.infant();

    let result: {
      originalAdult: number;
      adult: PriceFlights | undefined;
      originalChild: number;
      child: PriceFlights | undefined;
      originalInfant: number;
      infant: PriceFlights | undefined;
      ancillaries: number;
    } = {
      originalAdult: 0,
      adult: undefined,
      originalChild: 0,
      child: undefined,
      originalInfant: 0,
      infant: undefined,
      ancillaries: 0,
    };

    let prices =
      this.selectFlightState.selectedRoundTripFlight()?.flights[0].prices;
    if (prices) {
      if (adult > 0) {
        const adultPrice = prices!.find((p) => p.type === PassengerType.Adult);
        if (adultPrice) {
          result.adult = adultPrice;

          if (adultPrice.discount > 0) {
            result.originalAdult = adultPrice.price + adultPrice.discount;
          }
        }
      }

      if (child > 0) {
        const childPrice = prices!.find((p) => p.type === PassengerType.Child);
        if (childPrice) {
          result.child = childPrice;

          if (childPrice.discount > 0) {
            result.originalChild = childPrice.price + childPrice.discount;
          }
        }
      }

      if (infant > 0) {
        const infantPrice = prices!.find(
          (p) => p.type === PassengerType.Infant,
        );
        if (infantPrice) {
          result.infant = infantPrice;

          if (infantPrice.discount > 0) {
            result.originalInfant = infantPrice.price + infantPrice.discount;
          }
        }
      }
    }

    result.ancillaries = this.roundTripFlightAncillaries().reduce(
      (a, b) => a + b.totalPrice,
      0,
    );

    return result;
  });

  readonly flightAncillaries = computed(() => {
    const bookingRequest = this._bookingStore.bookingRequest();
    if (bookingRequest) {
      const ancillaries = bookingRequest.listPassenger
        .flatMap((p) => p.ancillaries)
        .filter((a) => a.leg === FlightType.one);

      if (ancillaries.length > 0) {
        const result = ancillaries.reduce((arr: Array<TotalAncillary>, anc) => {
          const index = arr.findIndex((a) => a.name === anc.name);
          if (index === -1) {
            arr.push({
              type: anc.type,
              name: anc.name,
              price: anc.price,
              leg: anc.leg,
              count: 1,
              totalPrice: anc.price,
            });
          } else {
            const item = arr[index];
            item.count += 1;
            item.totalPrice += anc.price;
          }
          return arr;
        }, []);

        return result;
      }

      return [];
    }
    return [];
  });
  readonly roundTripFlightAncillaries = computed(() => {
    const bookingRequest = this._bookingStore.bookingRequest();
    if (bookingRequest) {
      const ancillaries = bookingRequest.listPassenger
        .flatMap((p) => p.ancillaries)
        .filter((a) => a.leg === FlightType.two);

      if (ancillaries.length > 0) {
        const result = ancillaries.reduce((arr: Array<TotalAncillary>, anc) => {
          const index = arr.findIndex((a) => a.name === anc.name);
          if (index === -1) {
            arr.push({
              type: anc.type,
              name: anc.name,
              price: anc.price,
              leg: anc.leg,
              count: 1,
              totalPrice: anc.price,
            });
          } else {
            const item = arr[index];
            item.count += 1;
            item.totalPrice += anc.price;
          }
          return arr;
        }, []);

        return result;
      }

      return [];
    }
    return [];
  });

  readonly flightTotalPriceChange = computed(() => {
    const bookingChecked = this._bookingStore.bookingChecked();
    const flight = bookingChecked?.flights[0];
    if (flight && flight.newTotalFare > 0) {
      const ancillaries = this.flightAncillaries().reduce(
        (a, b) => a + b.totalPrice,
        0,
      );
      const currentTotalPrice = flight.totalAmt + ancillaries;
      const newTotalPrice =
        flight.newTotalFare + flight.totalServiceFee + ancillaries;
      return {
        current: currentTotalPrice,
        new: newTotalPrice,
      };
    }
    return null;
  });
  readonly roundTripFlightTotalPriceChange = computed(() => {
    const bookingChecked = this._bookingStore.bookingChecked();
    const flight = bookingChecked?.flights[1];
    if (flight && flight.newTotalFare > 0) {
      const ancillaries = this.roundTripFlightAncillaries().reduce(
        (a, b) => a + b.totalPrice,
        0,
      );
      const currentTotalPrice = flight.totalAmt + ancillaries;
      const newTotalPrice =
        flight.newTotalFare + flight.totalServiceFee + ancillaries;
      return {
        current: currentTotalPrice,
        new: newTotalPrice,
      };
    }
    return null;
  });

  ngOnInit(): void {}
}
